/**
 * CIFRATURA A BLOCCHI — formato LCB2 (2026-09-13)
 *
 * Serve a togliere il muro dei 1,5 GB per file. Il formato di oggi cifra il
 * file con UNA chiamata sola su tutto il contenuto: misurato, costa tre volte
 * la dimensione del file in memoria (250 MB di file -> 748 MB di browser).
 * Per 10 GB servirebbero 30 GB di RAM.
 *
 * Qui il file viene spezzato in blocchi da 4 MB, ognuno cifrato per conto suo.
 * La memoria non dipende piu' dalla dimensione del file.
 *
 * ── IL VINCOLO CHE DECIDE TUTTO IL RESTO ────────────────────────────────────
 *
 * L'IV di ogni blocco e' CASUALE e sta SCRITTO ACCANTO AI DATI, dentro il
 * blob. Non in una colonna del database.
 *
 * E' la lezione del 7 settembre 2026: quel giorno 2.610 caricamenti di un
 * cliente fallirono con errore 500 dopo che il file era gia' sul disco. Il
 * contenuto c'era, ma l'iv viveva SOLO nella riga del database che non era mai
 * nata. Quei 2,1 GB non sono decifrabili da nessuno, nemmeno dal proprietario
 * con la sua password: sono byte morti.
 *
 * Da qui la regola: non deve piu' esistere un istante in cui il contenuto e'
 * su disco e la chiave per aprirlo no. Tutto cio' che serve ad aprire il file
 * sta DENTRO il file, tranne la chiave dell'utente.
 *
 * Per lo stesso motivo anche la CEK incartata sta nell'intestazione. E' una
 * scelta diversa da quella scritta in PROGETTO-CIFRATURA-A-BLOCCHI.md, che la
 * voleva solo nel database "perche' chi ha il file senza il DB non deve poter
 * decifrare". Il prezzo di quella scelta e' che perdere una riga perde il
 * file; qui si accetta il contrario. Chi ruba il blob deve comunque avere la
 * password dell'utente per aprirlo: senza, la CEK incartata e' rumore.
 *
 * ── STRUTTURA ───────────────────────────────────────────────────────────────
 *
 *   INTESTAZIONE (79 byte, in chiaro)
 *     0   4   magic "LCB2"
 *     4   1   versione = 2
 *     5   4   dimensione del blocco, uint32 big-endian
 *     9   8   dimensione in chiaro, uint64 big-endian
 *    17   2   lunghezza della CEK incartata, uint16 big-endian
 *    19  60   CEK incartata: iv(12) || ciphertext(32) || tag(16)
 *
 *   BLOCCHI, uno dietro l'altro
 *     iv(12) || ciphertext(fino a 4 MB) || tag(16)
 *
 * Il numero di blocchi non e' scritto: si ricava da dimensione in chiaro e
 * dimensione del blocco, e cosi' i due valori non possono contraddirsi.
 *
 * ── PERCHE' L'AAD SERVE ANCHE CON L'IV CASUALE ──────────────────────────────
 *
 * Un IV casuale rende ogni blocco illeggibile senza la chiave, ma non impedisce
 * di SPOSTARLO: un blocco valido resta valido anche messo in un'altra
 * posizione. L'AAD lega ogni blocco alla sua posizione e al file intero, e GCM
 * lo include nel calcolo del tag senza cifrarlo:
 *
 *     AAD = versione(1) || indice(8) || totale blocchi(8) || dimensione(8)
 *
 * Ferma il riordino, la duplicazione, il troncamento e l'aggiunta di blocchi in
 * coda: il totale sta nell'AAD di OGNI blocco, quindi chi ne toglie tre lo fa
 * scoprire gia' dal primo che si prova ad aprire.
 */

export const MAGIC = new Uint8Array([0x4c, 0x43, 0x42, 0x32]); // "LCB2"
export const VERSIONE = 2;
export const DIMENSIONE_BLOCCO = 4 * 1024 * 1024;
export const LUNGHEZZA_IV = 12;
export const LUNGHEZZA_TAG = 16;
export const LUNGHEZZA_INTESTAZIONE = 79;

const subtle = () => globalThis.crypto.subtle;

function scriviUint64(vista, offset, valore) {
  // I file arrivano a 10 GB: oltre i 2^53 JavaScript perde precisione, ma
  // 10 GB sono 2^33. BigInt qui costerebbe solo complicazione.
  const alto = Math.floor(valore / 4294967296);
  const basso = valore >>> 0;
  vista.setUint32(offset, alto, false);
  vista.setUint32(offset + 4, basso, false);
}
function leggiUint64(vista, offset) {
  return vista.getUint32(offset, false) * 4294967296 + vista.getUint32(offset + 4, false);
}

/** Una chiave AES-256 nuova per questo file. */
export async function generaCek() {
  return subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Incarta la CEK con la chiave dell'utente: iv(12) || ciphertext(32) || tag(16). */
export async function incartaCek(cek, chiaveUtente) {
  const grezza = new Uint8Array(await subtle().exportKey('raw', cek));
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(LUNGHEZZA_IV));
  const chiusa = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv }, chiaveUtente, grezza));
  grezza.fill(0);
  const fuori = new Uint8Array(iv.length + chiusa.length);
  fuori.set(iv, 0); fuori.set(chiusa, iv.length);
  return fuori;
}

export async function apriCek(incartata, chiaveUtente) {
  const iv = incartata.slice(0, LUNGHEZZA_IV);
  const corpo = incartata.slice(LUNGHEZZA_IV);
  const grezza = await subtle().decrypt({ name: 'AES-GCM', iv }, chiaveUtente, corpo);
  return subtle().importKey('raw', grezza, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export function creaIntestazione({ dimensioneBlocco, dimensioneInChiaro, cekIncartata }) {
  const fuori = new Uint8Array(LUNGHEZZA_INTESTAZIONE);
  const vista = new DataView(fuori.buffer);
  fuori.set(MAGIC, 0);
  fuori[4] = VERSIONE;
  vista.setUint32(5, dimensioneBlocco, false);
  scriviUint64(vista, 9, dimensioneInChiaro);
  vista.setUint16(17, cekIncartata.length, false);
  fuori.set(cekIncartata, 19);
  return fuori;
}

export function leggiIntestazione(bytes) {
  if (bytes.length < LUNGHEZZA_INTESTAZIONE) throw new Error('Intestazione troppo corta');
  for (let i = 0; i < 4; i++) if (bytes[i] !== MAGIC[i]) throw new Error('Non e\' un file a blocchi (magic assente)');
  const vista = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const versione = bytes[4];
  if (versione !== VERSIONE) throw new Error('Versione del formato sconosciuta: ' + versione);
  const dimensioneBlocco = vista.getUint32(5, false);
  const dimensioneInChiaro = leggiUint64(vista, 9);
  const lunghezzaCek = vista.getUint16(17, false);
  const cekIncartata = bytes.slice(19, 19 + lunghezzaCek);
  const totaleBlocchi = dimensioneInChiaro === 0 ? 0 : Math.ceil(dimensioneInChiaro / dimensioneBlocco);
  return { versione, dimensioneBlocco, dimensioneInChiaro, cekIncartata, totaleBlocchi };
}

export function aad(indice, totaleBlocchi, dimensioneInChiaro) {
  const fuori = new Uint8Array(25);
  const vista = new DataView(fuori.buffer);
  fuori[0] = VERSIONE;
  scriviUint64(vista, 1, indice);
  scriviUint64(vista, 9, totaleBlocchi);
  scriviUint64(vista, 17, dimensioneInChiaro);
  return fuori;
}

/** Un blocco cifrato: iv(12) || ciphertext || tag(16). */
export async function cifraBlocco(cek, pezzo, indice, totaleBlocchi, dimensioneInChiaro) {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(LUNGHEZZA_IV));
  const chiuso = new Uint8Array(await subtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: aad(indice, totaleBlocchi, dimensioneInChiaro) }, cek, pezzo));
  const fuori = new Uint8Array(LUNGHEZZA_IV + chiuso.length);
  fuori.set(iv, 0); fuori.set(chiuso, LUNGHEZZA_IV);
  return fuori;
}

export async function decifraBlocco(cek, blocco, indice, totaleBlocchi, dimensioneInChiaro) {
  const iv = blocco.slice(0, LUNGHEZZA_IV);
  const corpo = blocco.slice(LUNGHEZZA_IV);
  return new Uint8Array(await subtle().decrypt(
    { name: 'AES-GCM', iv, additionalData: aad(indice, totaleBlocchi, dimensioneInChiaro) }, cek, corpo));
}

/** Quanto pesa un blocco cifrato, dato quello in chiaro. */
export const dimensioneBloccoCifrato = (inChiaro) => LUNGHEZZA_IV + inChiaro + LUNGHEZZA_TAG;

// ── LETTURA IN MEMORIA (2026-09-16) ─────────────────────────────────────────
//
// Miniatura e anteprima leggono un file intero e lo tengono in RAM per darlo
// a <img>/<video>/pdf.js. Lo scaricamento vero (scaricaABlocchi in
// caricamentoBlocchi.js) NON passa di qui: quello scrive un blocco per volta
// su disco e non riempie la memoria. Queste due funzioni servono solo alla
// lettura veloce, e vanno chiamate SOLO sotto un tetto di dimensione deciso
// da chi chiama (VaultPage), perche' qui il picco e' ~2x il file (il cifrato
// piu' il chiaro insieme). Non toccano lo storage ne' il caricamento.

/** Vero se i primi byte sono l'intestazione di un file a blocchi v2 ("LCB2"). */
export function sembraV2(bytes) {
  if (!bytes || bytes.length < MAGIC.length) return false;
  for (let i = 0; i < MAGIC.length; i++) if (bytes[i] !== MAGIC[i]) return false;
  return true;
}

/**
 * Decifra in memoria un intero file a blocchi v2 e ne restituisce un Blob.
 * `bytesOBlob` puo' essere un Blob (dallo scaricamento) o un Uint8Array.
 * `mimeType` da' il tipo al Blob cosi' <img>/<video> lo aprono; se manca il
 * Blob resta senza tipo. Lancia un errore pulito se l'intestazione non e'
 * v2, se il file e' troncato o se un blocco non si autentica (tag/chiave
 * sbagliati): mai byte spazzatura restituiti come se fossero il file.
 */
export async function decifraV2InMemoria(bytesOBlob, chiaveUtente, mimeType = '') {
  const bytes = bytesOBlob instanceof Uint8Array
    ? bytesOBlob
    : new Uint8Array(await bytesOBlob.arrayBuffer());
  if (!sembraV2(bytes)) throw new Error('Non e\' un file a blocchi v2');
  const h = leggiIntestazione(bytes);
  const cek = await apriCek(h.cekIncartata, chiaveUtente);
  const parti = [];
  let offset = LUNGHEZZA_INTESTAZIONE;
  for (let i = 0; i < h.totaleBlocchi; i++) {
    const restante = h.dimensioneInChiaro - i * h.dimensioneBlocco;
    const quanti = LUNGHEZZA_IV + Math.min(restante, h.dimensioneBlocco) + LUNGHEZZA_TAG;
    if (offset + quanti > bytes.length) {
      throw new Error(`File v2 troncato: manca il blocco ${i} di ${h.totaleBlocchi}`);
    }
    const blocco = bytes.subarray(offset, offset + quanti);
    // decifraBlocco lancia se il tag non torna: l'errore sale, non si ingoia.
    const chiaro = await decifraBlocco(cek, blocco, i, h.totaleBlocchi, h.dimensioneInChiaro);
    parti.push(chiaro);
    offset += quanti;
  }
  return new Blob(parti, mimeType ? { type: mimeType } : undefined);
}
